import React, { useEffect, useState, useRef } from "react";
import { X, CheckCircle, Lock, Lightning, QrCode, Copy, Spinner } from "@phosphor-icons/react";
import { api, brl } from "@/lib/api";
import { useFeatures } from "@/hooks/useFeatures";
import { toast } from "sonner";

export default function PlanGate() {
  const { gate, closeGate, refresh } = useFeatures();
  const [plans, setPlans] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState(null); // {plan_request_id, price, plan_name, qr_code_base64, pix_copia_cola}
  const pollRef = useRef(null);

  useEffect(() => {
    if (!gate.open) return;
    api.get("/public/plans").then(({ data }) => {
      setPlans(data.plans || []);
      setLabels(data.feature_labels || {});
    }).catch(() => {});
  }, [gate.open]);

  useEffect(() => {
    // Ao fechar modal, para o polling
    if (!gate.open && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      setCheckout(null);
    }
  }, [gate.open]);

  if (!gate.open) return null;

  const startPolling = (reqId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/plans/status/${reqId}`);
        if (data.status === "approved") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success(`Plano ${data.plan_name} ativado! Recarregando...`);
          await refresh();
          setTimeout(() => { closeGate(); window.location.reload(); }, 1200);
        }
      } catch {}
    }, 3500);
  };

  const buy = async (plan) => {
    setLoading(true);
    try {
      const { data } = await api.post("/plans/checkout", { plan_id: plan.id });
      setCheckout(data);
      startPolling(data.plan_request_id);
      toast.success("Pix gerado! Pague para ativar o plano.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar Pix");
    } finally { setLoading(false); }
  };

  const copyPix = () => {
    if (!checkout?.pix_copia_cola) return;
    navigator.clipboard.writeText(checkout.pix_copia_cola);
    toast.success("Pix copiado! Cole no seu banco.");
  };

  const featureLabel = labels[gate.requiredFeature] || gate.requiredFeature;

  // TELA DE PIX (checkout ativo)
  if (checkout) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/85 backdrop-blur-sm fadeUp" onClick={closeGate} data-testid="plan-checkout-overlay">
        <div className="relative w-full max-w-md bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="plan-checkout-modal">
          <button onClick={closeGate} data-testid="checkout-close" className="absolute top-4 right-4 btn-ghost !p-2"><X size={18} /></button>
          <div className="text-center">
            <div className="label-caps mb-1">Assinar plano</div>
            <h2 className="font-display font-black text-3xl tracking-tighter">
              {checkout.plan_name} · <span className="text-[var(--accent)]">{brl(checkout.price)}</span>
            </h2>
            <div className="mt-1 text-xs text-white/50">Ativação automática após confirmação do Pix</div>
          </div>

          {checkout.qr_code_base64 ? (
            <img src={`data:image/png;base64,${checkout.qr_code_base64}`} alt="QR Code Pix" className="w-56 h-56 mx-auto my-6 bg-white p-2 rounded-sm" data-testid="checkout-qr" />
          ) : (
            <div className="w-56 h-56 mx-auto my-6 border-2 border-dashed border-white/20 rounded-sm flex items-center justify-center">
              <QrCode size={48} className="text-white/30" />
            </div>
          )}

          <div className="relative mb-4">
            <textarea
              readOnly
              value={checkout.pix_copia_cola || ""}
              className="w-full text-[10px] bg-black/40 border border-[var(--border)] rounded-sm p-3 pr-12 h-20 resize-none font-mono text-white/80"
              data-testid="checkout-pix-copia-cola"
            />
            <button onClick={copyPix} data-testid="checkout-copy" className="absolute top-2 right-2 btn-ghost !p-2 hover:text-[var(--accent)]">
              <Copy size={14} />
            </button>
          </div>

          <div className="flex items-center gap-2 justify-center text-xs text-white/60 mb-2">
            <Spinner size={14} className="animate-spin text-[var(--accent)]" />
            Aguardando pagamento…
          </div>
          <div className="text-center text-[10px] text-white/40">
            Assim que o Pix cair, seu plano é ativado automaticamente
          </div>

          {checkout.mock && (
            <div className="mt-4 p-3 rounded-sm bg-[var(--accent)]/10 border border-[var(--accent)]/30 text-[10px] text-white/70 text-center">
              ⚠ Modo teste (mock) — admin precisa conectar Mercado Pago em <b>/app/settings</b> para Pix real
            </div>
          )}
        </div>
      </div>
    );
  }

  // TELA DE PLANOS
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm fadeUp" onClick={closeGate} data-testid="plan-gate-overlay">
      <div className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[var(--bg-base)] border border-[var(--border)] rounded-lg" onClick={(e) => e.stopPropagation()} data-testid="plan-gate-modal">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[var(--bg-base)] border-b border-[var(--border)] px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm border border-[var(--accent)] flex items-center justify-center">
              <Lock size={18} weight="fill" color="var(--accent)" />
            </div>
            <div>
              <div className="label-caps text-[10px]">Recurso bloqueado · Escolha um plano</div>
              <h2 className="font-display font-black text-2xl tracking-tighter leading-none mt-1">
                Libere <span className="text-[var(--accent)]">{featureLabel}</span>
              </h2>
            </div>
          </div>
          <button onClick={closeGate} data-testid="plan-gate-close" className="btn-ghost !p-2" title="Fechar (ou clique fora)">
            <X size={18} />
          </button>
        </div>

        {/* Plans */}
        <div className="p-8">
          <p className="text-white/60 text-sm mb-6">
            Este recurso está disponível em um dos planos abaixo. Escolha o ideal para você — sem letras miúdas, sem fidelidade. Pagamento via Pix, ativação automática.
          </p>
          {plans.length === 0 ? (
            <div className="text-center py-16 text-white/50">Nenhum plano disponível no momento. Fale com o admin.</div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {plans.map((p) => {
                const hasFeature = (p.features || []).includes(gate.requiredFeature);
                return (
                  <div key={p.id} data-testid={`plan-card-${p.name.toLowerCase()}`}
                    className={`card-flat p-6 flex flex-col ${hasFeature ? "border-[var(--accent)] ring-1 ring-[var(--accent)]/30" : "opacity-60"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-display font-black text-2xl tracking-tighter">{p.name}</div>
                      {hasFeature && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm border border-[var(--accent)] text-[var(--accent)] font-mono uppercase">Libera</span>
                      )}
                    </div>
                    <div className="font-display font-black text-4xl text-[var(--accent)] tracking-tighter">
                      {brl(p.price)}<span className="text-white/40 text-sm font-normal">/mês</span>
                    </div>
                    {p.description && <p className="text-xs text-white/50 mt-3 mb-4 min-h-[36px]">{p.description}</p>}
                    <ul className="space-y-2 mb-6 flex-1">
                      {(p.features || []).map((f) => (
                        <li key={f} className={`flex items-center gap-2 text-xs ${f === gate.requiredFeature ? "text-[var(--accent)] font-semibold" : "text-white/70"}`}>
                          <CheckCircle size={14} weight="fill" color={f === gate.requiredFeature ? "var(--accent)" : "var(--success)"} />
                          {labels[f] || f}
                        </li>
                      ))}
                    </ul>
                    <button disabled={loading || !hasFeature} onClick={() => buy(p)}
                      data-testid={`plan-buy-${p.name.toLowerCase()}`}
                      className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-bold transition ${hasFeature ? "bg-[var(--accent)] text-black hover:bg-[var(--accent)]/90" : "bg-white/5 text-white/40 cursor-not-allowed"}`}>
                      {loading ? <Spinner size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
                      {hasFeature ? `Assinar ${p.name}` : "Não libera este recurso"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-8 text-center text-xs text-white/40">
            Pix gerado no ato · Ativação automática · Sem fidelidade
          </div>
        </div>
      </div>
    </div>
  );
}
